import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Platform } from '../Icons.jsx';
import { PhoneFrame } from './PhoneFrame.jsx';
import { PlatformCard } from './PlatformCards.jsx';
import { getCharacterLimit, pickPlatformText, platformLabel } from './platformText.js';

export const BatchPreview = ({
  posts = [],
  platforms = [],
  brand = 'Your brand',
  targets = [],
  initialIndex = 0,
  initialPlatform = null,
  showActions = true,
  onEdit,
  onPublishNow,
  onClose,
  topic = null,
  batch = null,
  showHeader = true,
}) => {
  const { t } = useTranslation();
  const safePlatforms = platforms.length ? platforms : ['linkedin'];
  const [activePlatform, setActivePlatform] = useState(() => initialPlatform || safePlatforms[0]);
  const [postIndex, setPostIndex] = useState(initialIndex);
  const [slideIndex, setSlideIndex] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [reviewFilter, setReviewFilter] = useState('all');

  useEffect(() => {
    if (!safePlatforms.includes(activePlatform)) {
      setActivePlatform(safePlatforms[0]);
    }
  }, [safePlatforms.join('|')]);

  useEffect(() => {
    if (postIndex > Math.max(0, posts.length - 1)) {
      setPostIndex(0);
    }
  }, [posts.length]);

  useEffect(() => {
    setSlideIndex(0);
  }, [postIndex, activePlatform]);

  const total = posts.length;
  const currentPost = posts[postIndex] || null;
  const activeTarget = targetForPlatform(targets, activePlatform);
  const activeBrand = targetDisplayName(activeTarget, activePlatform, brand);
  const activeMeta = targetMetaLine(activeTarget, activePlatform, t);
  const currentText = useMemo(
    () => pickPlatformText(currentPost, activePlatform),
    [currentPost, activePlatform],
  );
  const charLimit = getCharacterLimit(activePlatform);
  const overLimit = currentText.length > charLimit;

  const goPrev = () => setPostIndex(index => Math.max(0, index - 1));
  const goNext = () => setPostIndex(index => Math.min(total - 1, index + 1));

  useEffect(() => {
    function onKey(event) {
      if (lightboxImage && event.key === 'Escape') {
        setLightboxImage(null);
        return;
      }
      if (event.target?.tagName?.match(/INPUT|TEXTAREA|SELECT/)) return;
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [total, lightboxImage]);

  if (!total) return null;

  const hasCarousel = currentPost?.format === 'carousel' && Array.isArray(currentPost.slides) && currentPost.slides.length > 0;
  const isGenerating = currentPost?.status === 'generating';
  const isTerminalOrScheduled = ['scheduled', 'publishing', 'published'].includes(currentPost?.status);
  const generationProgress = generationProgressFor(currentPost, batch);
  const progressIsIndeterminate = isGenerating && generationProgress <= 8;
  const generationSteps = generationStepsFor(batch, posts, t);
  const showGenerationSummary = !generationIsSettled(batch, posts);
  const visualCount = posts.filter(post => Array.isArray(post.mediaAssets) && post.mediaAssets.some(asset => asset?.publicUrl)).length;
  const nextScheduledAt = nextScheduledTime(batch, posts);
  const currentScheduledAt = postScheduleAt(currentPost);
  const scheduleSlots = scheduleSlotsForPosts(posts);
  const reviewPosts = useMemo(() => filteredReviewPosts(posts, reviewFilter), [posts, reviewFilter]);
  const showFilters = total >= 8 || posts.some(post => post.status === 'failed');

  return (
    <div className={'batch-preview ' + (!showHeader ? 'batch-preview-embedded' : '')}>
      {showHeader && (
        <div className="batch-preview-header">
          <div className="batch-preview-eyebrow">
            <Icon name="sparkles" size={12} /> {t('batchPreview.livePreview')}
            {batch?.topic && <span className="batch-preview-topic">· {batch.topic}</span>}
            {!batch?.topic && topic && <span className="batch-preview-topic">· {topic}</span>}
          </div>
          {onClose && (
            <button className="btn btn-icon btn-ghost" onClick={onClose} aria-label={t('batchPreview.closePreview')}>
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
      )}

      <div className="batch-preview-toolbar">
        <div className="platform-switcher">
          {safePlatforms.map(platform => (
            <button
              key={platform}
              className={'platform-tab ' + (activePlatform === platform ? 'on' : '')}
              onClick={() => setActivePlatform(platform)}
              title={platformLabel(platform)}
            >
              <Platform name={platform} size={16} />
              <span>{platformLabel(platform)}</span>
            </button>
          ))}
        </div>
        <div className="batch-preview-meta">
          <span className={'char-count ' + (overLimit ? 'over' : '')}>
            {currentText.length} / {charLimit}
          </span>
        </div>
      </div>

      {showGenerationSummary && (
        <div className="batch-generation-summary" role="status" aria-live="polite">
          <div className="batch-generation-copy">
            <strong>{readySummary(posts, batch, t)}</strong>
            <span>
              {visualCount > 0 ? t('batchPreview.visualsReady', { count: visualCount }) : t('batchPreview.visualsPreparing')}
              {nextScheduledAt ? ` · ${t('batchPreview.nextScheduled', { time: formatScheduleTime(nextScheduledAt, t) })}` : ''}
            </span>
          </div>
          <div className="batch-generation-steps">
            {generationSteps.map(step => (
              <span key={step.id} className={'batch-generation-step is-' + step.state}>
                <span className="batch-generation-step-dot" />
                {step.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {total > 1 && (
        <div className="batch-review-board" aria-label={t('batchPreview.reviewBoardLabel')}>
          <div className="batch-review-board-head">
            <div>
              <div className="eyebrow">{t('batchPreview.batchReview')}</div>
              <strong>{t('batchPreview.postsCount', { count: total })}</strong>
            </div>
            {showFilters && (
              <div className="batch-review-filters" role="tablist" aria-label={t('batchPreview.filterPostsLabel')}>
                {['all', 'ready', 'scheduled', 'failed', 'review'].map(filter => (
                  <button
                    key={filter}
                    type="button"
                    className={'batch-review-filter ' + (reviewFilter === filter ? 'on' : '')}
                    onClick={() => setReviewFilter(filter)}
                  >
                    {reviewFilterLabel(filter, t)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="batch-review-list">
            {reviewPosts.map(({ post, index }) => (
              <button
                key={post.id || index}
                type="button"
                className={'batch-review-item ' + (index === postIndex ? 'on ' : '') + 'status-' + (post.status || 'ready')}
                onClick={() => setPostIndex(index)}
              >
                <span className="batch-review-thumb">
                  {postThumbnail(post) ? <img src={postThumbnail(post)} alt="" loading="lazy" decoding="async" /> : <Icon name={post.status === 'failed' ? 'alert' : 'image'} size={16} />}
                </span>
                <span className="batch-review-copy">
                  <strong>{index + 1}. {postListTitle(post, t)}</strong>
                  <small>{postMetaLine(post, safePlatforms, t)}</small>
                </span>
                <span className={'batch-review-status status-' + (post.status || 'ready')}>{statusLabel(post.status, t)}</span>
              </button>
            ))}
          </div>
          <div className="batch-schedule-block" aria-label={t('batchPreview.scheduleSummaryLabel')}>
            <div>
              <div className="eyebrow">{isContentPlanBatch(batch) ? t('batchPreview.plannedFromContentPlan') : t('batchPreview.batchStatus')}</div>
              <strong>{scheduleBlockTitle(posts, batch, t)}</strong>
              <span>{nextScheduledAt ? t('batchPreview.nextLine', { time: formatScheduleTime(nextScheduledAt, t) }) : scheduleBlockDetail(posts, t)}</span>
            </div>
            {scheduleSlots.length > 0 && (
              <div className="batch-schedule-slots" aria-label={t('batchPreview.scheduledSlotsLabel')}>
                {scheduleSlots.slice(0, 4).map((slot) => (
                  <span key={`${slot.index}-${slot.at}`} className="batch-schedule-slot">
                    <Icon name="calendar" size={11} /> {slot.index + 1}: {formatScheduleTime(slot.at, t)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="batch-preview-stage">
        {total > 1 && (
          <button
            className="batch-nav batch-nav-prev"
            onClick={goPrev}
            disabled={postIndex === 0}
            aria-label={t('batchPreview.previousPost')}
          >
            <Icon name="chev_l" size={18} />
          </button>
        )}

        <PhoneFrame size="md" className={'platform-frame-' + activePlatform}>
          <PlatformCard
            platform={activePlatform}
            post={currentPost}
            brand={activeBrand}
            accountMeta={activeMeta}
            slideIndex={slideIndex}
            onSlideChange={setSlideIndex}
            onOpenImage={setLightboxImage}
          />
        </PhoneFrame>

        {total > 1 && (
          <button
            className="batch-nav batch-nav-next"
            onClick={goNext}
            disabled={postIndex >= total - 1}
            aria-label={t('batchPreview.nextPost')}
          >
            <Icon name="chev_r" size={18} />
          </button>
        )}
      </div>

      <div className="batch-preview-footer">
        {total > 1 ? (
          <div className="batch-counter">
            <span className="batch-counter-num">{postIndex + 1} / {total}</span>
            <div className="batch-counter-dots">
              {posts.map((_, i) => (
                <button
                  key={i}
                  className={'batch-dot ' + (i === postIndex ? 'on' : '') + ' status-' + (posts[i].status || 'ready')}
                  onClick={() => setPostIndex(i)}
                  aria-label={t('batchPreview.postNumber', { number: i + 1 })}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="batch-counter">
            <span className="batch-counter-num">{t('batchPreview.onePost')}</span>
          </div>
        )}

        {hasCarousel && (
          <div className="batch-carousel-hint">
            <Icon name="layers" size={11} /> {t('batchPreview.carouselSlides', { count: currentPost.slides.length })}
          </div>
        )}
      </div>

      {currentScheduledAt && (
        <div className="batch-current-slot">
          <Icon name="calendar" size={12} />
          <strong>{t('batchPreview.recommendedSlot')}</strong>
          <span>{formatScheduleTime(currentScheduledAt, t)} · {isContentPlanBatch(batch) ? t('batchPreview.plannedFromContentPlan') : t('batchPreview.scheduled')}</span>
        </div>
      )}

      {showActions && currentPost && !isGenerating && (
        <div className="batch-preview-actions">
          {onEdit && (
            <button className="btn btn-secondary btn-sm" onClick={() => onEdit(currentPost)}>
              <Icon name="edit" size={12} /> {t('batchPreview.editReview')}
            </button>
          )}
          <span style={{ flex: 1 }} />
          {isTerminalOrScheduled && <span className={'batch-status-pill status-' + currentPost.status}>{statusLabel(currentPost.status, t)}</span>}
          {onPublishNow && !['generating', 'publishing', 'published', 'failed'].includes(currentPost.status) && (
            <button className="btn btn-primary btn-sm" onClick={() => onPublishNow(currentPost, { mode: 'publish' })}>
              <Icon name="send" size={12} /> {t('batchPreview.publishNow')}
            </button>
          )}
        </div>
      )}

      {isGenerating && (
        <div className="batch-preview-status">
          <Icon name="sparkles" size={12} /> {t('batchPreview.aiWriting')}
          <span className={'batch-progress ' + (progressIsIndeterminate ? 'is-indeterminate' : '')}>
            <span
              className="batch-progress-fill"
              style={progressIsIndeterminate ? undefined : { width: `${generationProgress}%` }}
            />
          </span>
          <span className="batch-progress-label">{generationProgress}%</span>
        </div>
      )}

      {lightboxImage && (
        <div className="preview-lightbox" role="dialog" aria-modal="true" aria-label={t('batchPreview.fullImagePreview')} onClick={() => setLightboxImage(null)}>
          <button className="preview-lightbox-close" onClick={() => setLightboxImage(null)} aria-label={t('batchPreview.closeImagePreview')}>
            <Icon name="x" size={16} />
          </button>
          <img src={lightboxImage.src} alt={lightboxImage.alt || t('batchPreview.previewImage')} onClick={event => event.stopPropagation()} />
          {lightboxImage.label && <div className="preview-lightbox-caption">{lightboxImage.label}</div>}
        </div>
      )}
    </div>
  );
};

function filteredReviewPosts(posts, filter) {
  return posts
    .map((post, index) => ({ post, index }))
    .filter(({ post }) => {
      if (filter === 'ready') return ['ready', 'publishing', 'published'].includes(post.status);
      if (filter === 'scheduled') return post.status === 'scheduled';
      if (filter === 'failed') return post.status === 'failed';
      if (filter === 'review') return !['published', 'publishing', 'scheduled', 'failed'].includes(post.status);
      return true;
    });
}

function reviewFilterLabel(filter, t) {
  if (filter === 'ready') return t('batchPreview.filterReady');
  if (filter === 'scheduled') return t('batchPreview.filterScheduled');
  if (filter === 'failed') return t('batchPreview.filterFailed');
  if (filter === 'review') return t('batchPreview.filterNeedsReview');
  return t('batchPreview.filterAll');
}

function postThumbnail(post) {
  return firstImageUrl(
    post?.mediaAssets?.map(asset => asset?.publicUrl),
    post?.slides?.map(slide => slide?.imageUrl),
    post?.raw?.mediaPreview?.thumbnailUrl,
    post?.raw?.mediaPreview?.url,
  );
}

function firstImageUrl(...sources) {
  for (const source of sources) {
    if (typeof source === 'string' && /^https?:\/\//.test(source)) return source;
    if (Array.isArray(source)) {
      const found = firstImageUrl(...source);
      if (found) return found;
    }
  }
  return null;
}

function postListTitle(post, t) {
  return (post?.hook || post?.title || post?.topic || t('batchPreview.generatedPost')).slice(0, 90);
}

function postMetaLine(post, platforms, t) {
  const scheduleAt = postScheduleAt(post);
  const scheduleLabel = post.status === 'scheduled' ? t('batchPreview.scheduled') : t('batchPreview.slot');
  const schedule = scheduleAt ? ` · ${scheduleLabel} ${formatScheduleTime(scheduleAt, t)}` : '';
  const platformText = (platforms || []).map(platformLabel).join(', ');
  return `${platformText || t('batchPreview.platform')}${schedule}`;
}

function targetForPlatform(targets, platform) {
  return (targets || []).find(target => normalizePlatform(target.platform) === normalizePlatform(platform)) || null;
}

function generationProgressFor(post, batch) {
  const values = [post?.generationProgress, batch?.progress]
    .map(value => Number(value))
    .filter(value => Number.isFinite(value) && value > 0);
  const progress = values.length ? Math.max(...values) : 8;
  return Math.max(5, Math.min(100, Math.round(progress)));
}

function generationStepsFor(batch, posts, t) {
  const hasLinks = Array.isArray(batch?.referenceUrls) && batch.referenceUrls.length > 0;
  const hasImages = (Array.isArray(batch?.referenceAssetIds) && batch.referenceAssetIds.length > 0)
    || posts.some(post => Array.isArray(post.mediaAssets) && post.mediaAssets.length > 0)
    || posts.some(post => post.format === 'image_post' || post.format === 'carousel');
  const wantsSchedule = batch?.scheduleMode && batch.scheduleMode !== 'draft';
  const anyGenerating = posts.some(post => post.status === 'generating');
  const anyFailed = posts.some(post => post.status === 'failed');
  const anyReady = posts.some(post => ['ready', 'scheduled', 'publishing', 'published'].includes(post.status));
  const anyVisual = posts.some(post => Array.isArray(post.mediaAssets) && post.mediaAssets.some(asset => asset?.publicUrl));
  return [
    { id: 'context', label: t('batchPreview.stepPreparingContext'), state: anyReady || anyGenerating || anyFailed ? 'done' : 'active' },
    hasLinks ? { id: 'links', label: t('batchPreview.stepReadingLinks'), state: anyReady || anyGenerating || anyFailed ? 'done' : 'active' } : null,
    hasImages ? { id: 'images', label: t('batchPreview.stepCheckingImages'), state: anyVisual || anyReady || anyFailed ? 'done' : anyGenerating ? 'active' : 'waiting' } : null,
    { id: 'writing', label: t('batchPreview.stepWritingPosts'), state: anyReady || anyFailed ? 'done' : anyGenerating ? 'active' : 'waiting' },
    hasImages ? { id: 'visuals', label: t('batchPreview.stepCreatingVisuals'), state: anyVisual ? 'done' : anyFailed ? 'blocked' : anyGenerating ? 'active' : 'waiting' } : null,
    wantsSchedule ? { id: 'schedule', label: t('batchPreview.stepScheduling'), state: posts.some(post => ['scheduled', 'publishing', 'published'].includes(post.status)) || nextScheduledTime(batch, posts) ? 'done' : anyFailed ? 'blocked' : anyGenerating ? 'waiting' : 'active' } : null,
    { id: 'ready', label: t('batchPreview.stepReady'), state: posts.length && posts.every(post => ['ready', 'scheduled', 'published', 'failed'].includes(post.status)) ? 'done' : anyFailed ? 'blocked' : 'waiting' },
  ].filter(Boolean);
}

function generationIsSettled(batch, posts) {
  if (!posts.length) return false;
  const generatingCount = Number(batch?.generatingCount ?? 0);
  return generatingCount <= 0 && posts.every(post => ['ready', 'scheduled', 'publishing', 'published', 'failed'].includes(post.status));
}

function readySummary(posts, batch, t) {
  const total = Math.max(1, Number(batch?.totalItems || 0), posts.length);
  const ready = Math.max(Number(batch?.readyCount || 0), posts.filter(post => ['ready', 'scheduled', 'publishing', 'published'].includes(post.status)).length);
  const failed = Math.max(Number(batch?.failedCount || 0), posts.filter(post => post.status === 'failed').length);
  const scheduled = posts.filter(post => post.status === 'scheduled').length;
  const published = posts.filter(post => post.status === 'published').length;
  const publishing = posts.filter(post => post.status === 'publishing').length;
  if (scheduled > 0 && scheduled + published + publishing + failed >= total) return t('batchPreview.postsScheduled', { count: scheduled });
  if (published > 0 && published + failed >= total) return t('batchPreview.postsPublished', { count: published });
  if (failed > 0 && ready > 0) return t('batchPreview.readyWithRetry', { ready, total, failed });
  if (failed > 0) return t('batchPreview.postsNeedRetry', { count: failed });
  return t('batchPreview.readyOfTotal', { ready, total });
}

function nextScheduledTime(batch, posts) {
  const postTimes = posts.map(postScheduleAt)
    .filter(value => value && !Number.isNaN(Date.parse(value)))
    .sort((a, b) => Date.parse(a) - Date.parse(b));
  const nextPostTime = postTimes.find(value => Date.parse(value) > Date.now());
  if (nextPostTime) return nextPostTime;
  const times = [batch?.schedulePlan?.startAt, batch?.scheduleStartAt]
    .filter(value => value && !Number.isNaN(Date.parse(value)))
    .sort((a, b) => Date.parse(a) - Date.parse(b));
  return times.find(value => Date.parse(value) > Date.now()) || null;
}

function scheduleSlotsForPosts(posts) {
  return posts
    .map((post, index) => ({ index, at: postScheduleAt(post) }))
    .filter(slot => slot.at && !Number.isNaN(Date.parse(slot.at)))
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

function postScheduleAt(post) {
  return post?.publishScheduleAt || post?.publishSummary?.lastScheduledAt || null;
}

function scheduleBlockTitle(posts, batch, t) {
  const total = Math.max(1, Number(batch?.totalItems || 0), posts.length);
  const scheduled = posts.filter(post => post.status === 'scheduled').length;
  const published = posts.filter(post => post.status === 'published').length;
  const failed = posts.filter(post => post.status === 'failed').length;
  if (scheduled > 0) return t('batchPreview.postsScheduled', { count: scheduled });
  if (published > 0) return t('batchPreview.postsPublished', { count: published });
  if (failed > 0) return t('batchPreview.postsNeedRetry', { count: failed });
  return t('batchPreview.postsInReview', { count: total });
}

function scheduleBlockDetail(posts, t) {
  const ready = posts.filter(post => post.status === 'ready').length;
  if (ready > 0) return t('batchPreview.readyForReview', { ready });
  return t('batchPreview.noScheduledSlot');
}

function isContentPlanBatch(batch) {
  return batch?.schedulePlan?.cadence === 'weekly_spread' || batch?.scheduleCadence === 'weekly_spread' || /Content Plan/i.test(batch?.goal || batch?.contextNote || '');
}

function formatScheduleTime(value, t) {
  try {
    return new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' }).format(new Date(value));
  } catch {
    return value ? String(value) : (t ? t('batchPreview.soon') : 'soon');
  }
}

function targetDisplayName(target, platform, fallback) {
  const value = target?.name || target?.displayName || target?.socialAccount?.displayName;
  if (value && !isOpaqueProviderName(value, target?.externalAccountId || target?.socialAccount?.externalAccountId)) return value;
  return fallback && fallback !== 'Your brand' ? fallback : platformLabel(platform);
}

function targetMetaLine(target, platform, t) {
  const capabilities = target?.capabilities || target?.socialAccount?.capabilities || {};
  const count = firstNumber(capabilities.subscriberCount, capabilities.subscribers, capabilities.followerCount, capabilities.followers, capabilities.memberCount, capabilities.members);
  if (count !== null) return platform === 'telegram'
    ? t('batchPreview.subscribersCount', { value: formatCompactNumber(count) })
    : t('batchPreview.followersCount', { value: formatCompactNumber(count) });
  return target?.secondaryText || (platform === 'telegram' ? t('batchPreview.telegramChannel') : t('batchPreview.platformAccount', { platform: platformLabel(platform) }));
}

function firstNumber(...values) {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number) && number >= 0) return number;
  }
  return null;
}

function formatCompactNumber(value) {
  try {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  } catch {
    return String(value);
  }
}

function isOpaqueProviderName(value, externalAccountId) {
  return /^\d{8,}$/.test(String(value || '')) || (externalAccountId && value === externalAccountId);
}

function normalizePlatform(platform) {
  return platform === 'twitter' ? 'x' : platform;
}

function statusLabel(status, t) {
  if (status === 'published') return t('batchPreview.statusPublished');
  if (status === 'publishing') return t('batchPreview.statusPublishing');
  if (status === 'scheduled') return t('batchPreview.statusScheduled');
  if (status === 'failed') return t('batchPreview.statusFailed');
  return t('batchPreview.statusReady');
}

export default BatchPreview;
